// src/components/layout/WhatsAppFloat.jsx
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const WhatsAppFloat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCallOpen, setIsCallOpen] = useState(false);
  const [isWhatsAppOpen, setIsWhatsAppOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const branches = [
    { city: "VIJAYAWADA", phone: "+919490948081", label: "Main Branch" },
    { city: "KPHB HYDERABAD", phone: "+918121222888", label: "JNTU / KPHB" },
    { city: "NALLAGANDLA", phone: "+919542027700", label: "BHEL / Nallagandla" }
  ];

  const defaultMessage = encodeURIComponent("Hello! I'd like to book an appointment.");
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsCallOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScrollTopVisibility = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScrollTopVisibility, { passive: true });
    return () => window.removeEventListener('scroll', handleScrollTopVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
  };

  const handleCallNow = () => {
    setIsCallOpen(!isCallOpen);
    setIsOpen(false);
    setIsWhatsAppOpen(false);
  };

  const handleWhatsAppToggle = () => {
    setIsOpen(!isOpen);
    setIsCallOpen(false);
    setIsWhatsAppOpen(false);
  };

  const handleMobileCall = () => {
    setIsCallOpen(true);
    setIsWhatsAppOpen(false);
  };

  const handleMobileWhatsApp = () => {
    setIsWhatsAppOpen(true);
    setIsCallOpen(false);
  };

  const handleBookAppointment = () => {
    setIsOpen(false);
    setIsCallOpen(false);
    setIsWhatsAppOpen(false);
    navigate('/book-appointment');
  };

  // Multiple fallback methods to open phone dialer
  const handlePhoneCall = (phoneNumber) => {
    setIsCallOpen(false);
    
    // Method 1: Standard tel: link
    const telLink = `tel:${phoneNumber}`;
    
    // Method 2: Try window.location.href
    try {
      window.location.href = telLink;
    } catch (e) {
      // Method 3: Fallback to window.open
      try {
        window.open(telLink, '_self');
      } catch (e2) {
        // Method 4: Create and click a hidden anchor element
        try {
          const anchor = document.createElement('a');
          anchor.href = telLink;
          anchor.style.display = 'none';
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
        } catch (e3) {
          console.error('All phone call methods failed:', e3);
        }
      }
    }
  };

  return (
    <>
      {/* Desktop Floating Actions - Visible only on md and above */}
      <div
        className="hidden md:flex fixed top-1/2 -translate-y-1/2 right-[max(1.5rem,env(safe-area-inset-right))] z-[999] flex-col items-end pointer-events-none"
        ref={menuRef}
      >
        {/* Scroll to Top Button */}
        <button
          onClick={scrollToTop}
          aria-label="Scroll to top"
          className={`fixed right-4 sm:right-6 top-1/2 -translate-y-1/2 z-50
            bg-blue-600 hover:bg-blue-700 text-white
            w-11 h-11 sm:w-12 sm:h-12 rounded-full
            flex items-center justify-center shadow-lg
            transition-all duration-300 ease-out
            ${showScrollTop ? 'opacity-100 translate-x-0 pointer-events-auto' : 'opacity-0 translate-x-4 pointer-events-none'}`}
        >
          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
          </svg>
        </button>

        {/* Buttons + Popups Container */}
        <div className="relative pointer-events-auto flex flex-col items-end gap-3">

          {/* Call Menu Box */}
          <div
            className={`absolute bottom-full right-0 mb-4 w-72 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden transition-all duration-300 origin-bottom-right
              ${isCallOpen
                ? 'opacity-100 scale-100 translate-y-0 visible pointer-events-auto'
                : 'opacity-0 scale-90 translate-y-10 invisible pointer-events-none'
              }`}
          >
    <div className="bg-blue-600 p-4 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="fas fa-phone text-2xl" />
                </div>
                <div>
                  <h3 className="font-bold text-sm">Call us now</h3>
                  <p className="text-[10px] opacity-90 uppercase tracking-wider">We're here to help</p>
                </div>
              </div>
            </div>

            <div className="p-3 bg-gray-50 space-y-2">
              {branches.map((branch, index) => (
                <div
                  key={index}
                  onClick={() => handlePhoneCall(branch.phone)}
      className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-200
hover:border-blue-600 hover:bg-blue-100 hover:shadow-md transition-all group cursor-pointer"
                >
                  <div>
                    <div className="text-xs font-black text-blue-600 mb-0.5">{branch.city}</div>
                    <div className="text-sm font-bold text-gray-800">{branch.label}</div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-blue-100
group-hover:bg-blue-600 flex items-center justify-center  group-hover:text-white transition-colors">
                    <i className="fas fa-phone text-lg" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* WhatsApp Menu Box */}
          <div
            className={`absolute bottom-full right-0 mb-4 w-72 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden transition-all duration-300 origin-bottom-right
              ${isOpen
                ? 'opacity-100 scale-100 translate-y-0 visible pointer-events-auto'
                : 'opacity-0 scale-90 translate-y-10 invisible pointer-events-none'
              }`}
          >
            <div className="bg-[#25D366] p-4 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="fab fa-whatsapp text-2xl" />
                </div>
                <div>
                  <h3 className="font-bold text-sm">Chat with us</h3>
                  <p className="text-[10px] opacity-90 uppercase tracking-wider">Typically replies in minutes</p>
                </div>
              </div>
            </div>

            <div className="p-3 bg-gray-50 space-y-2">
              {branches.map((branch, index) => (
                <a
                  key={index}
                  href={`https://wa.me/${branch.phone}?text=${defaultMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100 
                             hover:border-green-400 hover:shadow-md transition-all group"
                >
                  <div>
                    <div className="text-xs font-black text-blue-600 mb-0.5">{branch.city}</div>
                    <div className="text-sm font-bold text-gray-800">{branch.label}</div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center group-hover:bg-[#25D366] group-hover:text-white transition-colors">
                    <i className="fab fa-whatsapp text-lg" />
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Call Now Button */}
          <button
            onClick={handleCallNow}
className={`px-4 py-2.5 bg-blue-600 text-white text-xs font-bold rounded-lg shadow-lg whitespace-nowrap hover:bg-blue-700 transition-all flex items-center gap-2
${isCallOpen ? 'bg-red-500 hover:bg-red-600' : ''}`}   >
            <i className={`${isCallOpen ? 'fas fa-times' : 'fas fa-phone'}`} />
            {isCallOpen ? 'Close' : 'Call Now'}
          </button>

          {/* WhatsApp Button */}
          <button
            onClick={handleWhatsAppToggle}
            className={`relative flex items-center justify-center w-14 h-14 md:w-16 md:h-16
                       rounded-full shadow-xl transition-all duration-500 pointer-events-auto
                       hover:scale-110 active:scale-95 focus:outline-none
                       ${isOpen ? 'bg-blue-600 rotate-[360deg]' : 'bg-[#25D366]'}`}
          >
            {!isOpen && !isCallOpen && (
              <span className="absolute inset-0 rounded-full bg-[#25D366]/40 animate-ping pointer-events-none" />
            )}
            <div className="relative z-10">
              {isOpen ? (
                <i className="fas fa-times text-xl text-white" />
              ) : (
                <i className="fab fa-whatsapp text-3xl md:text-4xl text-white" />
              )}
            </div>
          </button>

          {/* Book Appointment Button */}
          <button
            onClick={handleBookAppointment}
            className="px-4 py-2.5 bg-blue-600 text-white text-xs font-bold rounded-lg shadow-lg whitespace-nowrap hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <i className="fas fa-calendar-check" />
            Book Appointment
          </button>
        </div>
      </div>

      {/* Mobile Bottom Navigation Bar - 3 buttons */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-[999] bg-white border-t border-gray-200 shadow-lg">
        <div className="flex items-center justify-around py-2.5 px-2">
          {/* Book Appointment Button - 2 lines */}
          <button
            onClick={handleBookAppointment}
            className="flex flex-col items-center justify-center px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors active:scale-95"
          >
            <i className="fas fa-calendar-check text-xl text-blue-600" />
            <span className="text-[10px] font-semibold text-gray-700 mt-1 text-center leading-tight">Book<br/>Appointment</span>
          </button>

          {/* Call Now Button - Single line */}
          <button
            onClick={handleMobileCall}
            className="flex flex-col items-center justify-center px-3 py-1.5 rounded-lg hover:bg-green-50 transition-colors active:scale-95"
          >
            <i className="fas fa-phone text-xl text-[#4CAF50]" />
            <span className="text-[10px] font-semibold text-gray-700 mt-1 whitespace-nowrap">Call Now</span>
          </button>

          {/* WhatsApp Button - Single line */}
          <button
            onClick={handleMobileWhatsApp}
            className="flex flex-col items-center justify-center px-3 py-1.5 rounded-lg hover:bg-green-50 transition-colors active:scale-95"
          >
            <i className="fab fa-whatsapp text-xl text-[#25D366]" />
            <span className="text-[10px] font-semibold text-gray-700 mt-1 whitespace-nowrap">WhatsApp</span>
          </button>
        </div>
      </div>

      {/* Call Modal for Mobile */}
      {isCallOpen && (
        <div className="md:hidden fixed inset-0 z-[9999] bg-black/50 flex items-end justify-center">
          <div className="bg-white rounded-t-3xl w-full max-w-md max-h-[70vh] overflow-y-auto animate-slide-up">
<div className="sticky top-0 bg-blue-600 p-3 text-white rounded-t-3xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="fas fa-phone text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm">Call us now</h3>
                    <p className="text-[9px] opacity-90 uppercase tracking-wider">We're here to help</p>
                  </div>
                </div>
                <button onClick={() => setIsCallOpen(false)} className="text-white/80 hover:text-white">
                  <i className="fas fa-times text-xl" />
                </button>
              </div>
            </div>

            <div className="p-3 bg-gray-50 space-y-2">
              {branches.map((branch, index) => (
                <div
                  key={index}
                  onClick={() => handlePhoneCall(branch.phone)}
                  className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100 
                             hover:border-blue-600 hover:shadow-md transition-all group cursor-pointer"
                >
                  <div>
                    <div className="text-[11px] font-black text-blue-600 mb-0.5">{branch.city}</div>
                    <div className="text-xs font-bold text-gray-800">{branch.label}</div>
                  </div>
  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">      <i className="fas fa-phone text-base" />
                  </div>
                </div>
              ))}
              <button
                onClick={() => setIsCallOpen(false)}
                className="w-full py-2.5 text-gray-600 font-medium text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Modal for Mobile */}
      {isWhatsAppOpen && (
        <div className="md:hidden fixed inset-0 z-[9999] bg-black/50 flex items-end justify-center">
          <div className="bg-white rounded-t-3xl w-full max-w-md max-h-[70vh] overflow-y-auto animate-slide-up">
            <div className="sticky top-0 bg-[#25D366] p-3 text-white rounded-t-3xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="fab fa-whatsapp text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm">Chat with us</h3>
                    <p className="text-[9px] opacity-90 uppercase tracking-wider">Typically replies in minutes</p>
                  </div>
                </div>
                <button onClick={() => setIsWhatsAppOpen(false)} className="text-white/80 hover:text-white">
                  <i className="fas fa-times text-xl" />
                </button>
              </div>
            </div>

            <div className="p-3 bg-gray-50 space-y-2">
              {branches.map((branch, index) => (
                <a
                  key={index}
                  href={`https://wa.me/${branch.phone}?text=${defaultMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100 
                             hover:border-green-400 hover:shadow-md transition-all group"
                  onClick={() => setIsWhatsAppOpen(false)}
                >
                  <div>
                    <div className="text-[11px] font-black text-blue-600 mb-0.5">{branch.city}</div>
                    <div className="text-xs font-bold text-gray-800">{branch.label}</div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center group-hover:bg-[#25D366] group-hover:text-white transition-colors">
                    <i className="fab fa-whatsapp text-base" />
                  </div>
                </a>
              ))}
              <button
                onClick={() => setIsWhatsAppOpen(false)}
                className="w-full py-2.5 text-gray-600 font-medium text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WhatsAppFloat;